import { RootState, SetState } from '@/store/useStore.ts';
import {
  GeolocationContext,
  GeolocationSlice,
  GeolocationStatus,
  GeolocationStoreParts,
} from '@/store/slices/geolocation/types';
import { emptySliceError } from '../sliceError';

const defaultGeolocationStatus: GeolocationStatus = {
  geolocationInProgress: false,
  geolocationRejected: false,
  geolocationFailed: false,
  geolocationSuccess: false,
  previouslyVerified: false,
};

const defaultGeolocationContext: GeolocationContext = {
  geolocationToken: null,
};

const defaults = {
  [GeolocationStoreParts.geolocationContext]: defaultGeolocationContext,
  [GeolocationStoreParts.geolocationStatus]: defaultGeolocationStatus,
  [GeolocationStoreParts.geolocationError]: emptySliceError(),
};
export const geolocationSlice = ({
  set,
}: SetState<RootState>): GeolocationSlice => ({
  geolocationResetRevision: 0,
  geolocationContext: { ...defaults[GeolocationStoreParts.geolocationContext] },
  geolocationStatus: { ...defaults[GeolocationStoreParts.geolocationStatus] },
  geolocationError: {
    ...defaults[GeolocationStoreParts.geolocationError],
  },
  preChecksComplete: false,
  retryCount: 0,

  setGeolocationContext: (newState) =>
    set(
      (state: RootState) => ({
        ...state,
        geolocationContext: { ...state.geolocationContext, ...newState },
      }),
      false,
      'geolocation/setGeolocationContext',
    ),

  setGeolocationStatus: (newState) =>
    set(
      (state: RootState) => ({
        ...state,
        geolocationStatus: { ...state.geolocationStatus, ...newState },
      }),
      false,
      'geolocation/setGeolocationStatus',
    ),

  setGeolocationPassed: (geolocationToken) =>
    set(
      (state: RootState) => ({
        ...state,
        geolocationContext: {
          ...state.geolocationContext,
          geolocationToken,
        },
        geolocationStatus: {
          ...state.geolocationStatus,
          geolocationInProgress: false,
          geolocationSuccess: true,
        },
      }),
      false,
      'geolocation/setGeolocationPassed',
    ),

  setGeolocationError: (newState) =>
    set(
      (state: RootState) => ({
        ...state,
        geolocationError: {
          ...state.geolocationError,
          ...newState,
        },
      }),
      false,
      'geolocation/setGeolocationError',
    ),

  setPreChecksComplete: (complete: boolean) =>
    set(
      (state: RootState) => ({
        ...state,
        preChecksComplete: complete,
      }),
      false,
      'geolocation/setPreChecksComplete',
    ),

  incrementRetryCount: () =>
    set(
      (state: RootState) => ({
        ...state,
        retryCount: state.retryCount + 1,
      }),
      false,
      'geolocation/incrementRetryCount',
    ),

  resetGeolocationPart: (part: GeolocationStoreParts) =>
    set(
      (state) => ({
        ...state,
        [part]: { ...defaults[part] },
        geolocationResetRevision: state.geolocationResetRevision + 1,
      }),
      false,
      `geolocation/reset/${part}`,
    ),

  resetGeolocationForRelocate: () =>
    set(
      (state) => ({
        ...state,
        geolocationStatus: {
          ...defaults[GeolocationStoreParts.geolocationStatus],
        },
        geolocationError: {
          ...defaults[GeolocationStoreParts.geolocationError],
        },
        geolocationResetRevision: state.geolocationResetRevision + 1,
      }),
      false,
      'geolocation/resetForRelocate',
    ),

  resetAllGeolocationParts: () =>
    set(
      (state) => ({
        ...state,
        geolocationContext: {
          ...defaults[GeolocationStoreParts.geolocationContext],
        },
        geolocationStatus: {
          ...defaults[GeolocationStoreParts.geolocationStatus],
        },
        geolocationError: {
          ...defaults[GeolocationStoreParts.geolocationError],
        },
        retryCount: 0,
        geolocationResetRevision: state.geolocationResetRevision + 1,
      }),
      false,
      'geolocation/resetGeolocation',
    ),
});
